import React, { useState, useEffect } from "react";
import authCss from "./ConversionAuth.module.css";
import MenuBar from "../UI/Menu/Menu";
import { Input } from "../UI/Input/Input";
import { ButtonSubmitBlack } from "../UI/Buttons/Buttons";
import { Send, AutoAwesome, TrendingUp, Security } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ThreeCircles } from 'react-loader-spinner';
import { useDispatch } from "react-redux";
import { setEmail, setFetching } from "../../redux/states";
import { checkAuthenticatedUser } from "../../utils/client-functions";
import { useErrorSetter } from '../../hooks/useUiMessages';
import { jwtDecode } from "jwt-decode";
import { GoogleLogin } from '@react-oauth/google';

const screenWidth = window.innerWidth;

const Login = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [pwdRec, setPwdRec] = useState(false);
    const [data, setData] = useState({
        email: '',
        password: ''
    });

    const errorSetter = useErrorSetter();
    const queryString = location.search.slice(1);

    useEffect(() => {
        const isUserAuthenticated = async () => {
            try {
                await checkAuthenticatedUser();
                if (queryString.length >= 1)  {
                    if (queryString === 'pricing') {
                        navigate(`/#pricing`);
                    } else {
                        navigate(`/user/dashboard/${queryString}`);
                    }
                } else {
                    navigate('/');
                }
            } catch (error) {
                // not authenticated, stay here
            }
        }
        isUserAuthenticated();
    }, [navigate, queryString]);

    const handleFormSubmitLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const response = await axios.post('/auth/login', {
                email: data.email,
                password: data.password
            });
            localStorage.setItem('token', response?.data?.user);
            setLoading(false);
            
            if (queryString.length >= 1)  {
                if (queryString === 'pricing') {
                    navigate(`/#pricing`);
                } else {
                    navigate(`/user/dashboard/${queryString}`);
                }
            } else {
                navigate('/');
            }
        } catch (error) {
            setLoading(false);
            if (error?.response?.data?.error === "Unverified User") {
                errorSetter("Unverified User, Redirecting you to verify");
                dispatch(setEmail(error?.response?.data?.email));
                setTimeout(() => navigate("/verify"), 5000);
            } else {
                errorSetter(error?.response?.data?.message || 'Login failed');
            }
        }
    };

    const handleFormSubmitPwdRecovery = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await axios.post('/auth/pwd-recover', { email: data.email });
            setLoading(false);
            dispatch(setEmail(data.email));
            navigate('/reset-password');
        } catch (error) {
            setLoading(false);
            errorSetter(error?.response?.data?.message || 'Process failed');
        }
    };

    const handleGoogleLogin = async (credentialResponse) => {
        dispatch(setFetching(true));
        try {
            const token = credentialResponse.credential;
            if(token) {
                const decodedToken = jwtDecode(token);
                const userData = {
                    email: decodedToken.email,
                    firstName: decodedToken.given_name,
                    lastName: decodedToken.family_name,
                    password: decodedToken.jti
                };
                const response = await axios.post('/auth/google-register', userData);
                localStorage.setItem('token', response?.data?.user);
                dispatch(setFetching(false));
                
                if (queryString.length >= 1)  {
                    if (queryString === 'pricing') {
                        navigate(`/#pricing`);
                    } else {
                        navigate(`/user/dashboard/${queryString}`);
                    }
                } else {
                    navigate('/');
                }
            } else {
                dispatch(setFetching(false));
                errorSetter('Process Failed, Try Again');
            }
        } catch (error) {
            dispatch(setFetching(false));
            errorSetter('Bad Response, Try Again');
        }
    };

    const handleInputChange = (prop) => (event) => {
        setData({ ...data, [prop]: event.target.value});
    };

    return (
        <div>
            <MenuBar />
            <div className={authCss.authContainer}>
                
                <div className={authCss.blobBackground}>
                    <div className={authCss.blobAnimated} style={{ width: '300px', height: '300px', background: 'radial-gradient(circle, rgba(95,190,197,0.3) 0%, rgba(0,0,0,0) 70%)', borderRadius: '50%' }}></div>
                    <div className={authCss.blobAnimated} style={{ width: '400px', height: '400px', background: 'radial-gradient(circle, rgba(23,60,80,0.4) 0%, rgba(0,0,0,0) 70%)', borderRadius: '50%' }}></div>
                </div>

                <div className={authCss.formContainer}>
                    <div className={authCss.splitLayout}>
                        <div className={authCss.leftPanel}>
                            <h2>Welcome Back!</h2>
                            <p style={{fontSize: '1.1rem', lineHeight: '1.5', opacity: 0.9}}>Your personalized Bubble Ai assistant is ready to help you conquer the day.</p>
                            
                            <ul className={authCss.benefitList}>
                                <li><AutoAwesome /> <span>Continue your optimized job applications</span></li>
                                <li><TrendingUp /> <span>Master your next exam with AI simulators</span></li>
                                <li><Security /> <span>Secure and private data handling</span></li>
                            </ul>

                            <div className={authCss.socialProof}>
                                <p>"Bubble Ai saved me 10 hours a week on applications. It feels like magic."</p>
                                <span className={authCss.author}> Alex M., Landed Dream Job</span>
                            </div>
                        </div>

                        <div className={authCss.rightPanel}>
                            <div className={authCss.formInner}>
                                <h2>{!pwdRec ? "Sign In" : "Recover Password"}</h2>
                                <p className={authCss.subtitle}>
                                    {!pwdRec ? "Streamline your career journey" : "Enter your email to reset"}
                                </p>

                                <form onSubmit={!pwdRec ? handleFormSubmitLogin : handleFormSubmitPwdRecovery}>
                                    {!pwdRec && 
                                        <div>
                                            <div className={authCss.googleBtnWrapper}>
                                                <GoogleLogin
                                                    onSuccess={handleGoogleLogin}
                                                    onError={() => errorSetter('Login Failed')}
                                                    auto_select={true}
                                                    shape='pill'
                                                    text="continue_with"
                                                    width={screenWidth < 450 ? '300px' : '360px'}
                                                />
                                            </div>
                                            <div className={authCss.divider}>OR</div>
                                        </div>
                                    }
                                    
                                    <div className={authCss.inputGroup}>
                                        <Input placeholder="Email Address..." inputType="email" inputGridSm={12} onChange={handleInputChange('email')} /> 
                                    </div>
                                    
                                    {!pwdRec && 
                                        <div className={authCss.inputGroup}>
                                            <Input placeholder="Password..." inputType="password" inputGridSm={12} onChange={handleInputChange('password')} />
                                        </div>
                                    }

                                    {!pwdRec && 
                                        <div className={authCss.forgotPasswordWrapper}>
                                            <span className={authCss.regOnLinePage} onClick={() => navigate("/join-bubble")}>Create Account</span>
                                            <span className={authCss.pwdRec} onClick={() => setPwdRec(true)}>Forgot Password?</span>
                                        </div>
                                    }

                                    {pwdRec &&
                                        <div className={authCss.forgotPasswordWrapper}>
                                            <span className={authCss.pwdRec} onClick={() => setPwdRec(false)}>Back to Login</span>
                                        </div>
                                    }

                                    <div className={authCss.submitBtnWrapper}> 
                                        <ButtonSubmitBlack disabled={loading} type="submit">
                                            {!loading ? (!pwdRec ? <span>Sign In <Send style={{fontSize: '1.2rem', marginLeft: '5px', verticalAlign: 'middle'}}/></span> : <span>Send Link</span>) : 
                                            <ThreeCircles
                                                height="25"
                                                width="25"
                                                color="#FFFFFF"
                                                visible={true}
                                            />}
                                        </ButtonSubmitBlack>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>        
    )
}
export default Login;
